import { useAppDispatch } from "./reduxHook";
import { usePatchSpecimenMutation } from "../actions/specimen";
import { usePatchCaptureMutation } from "../actions/capture";
import { catalogApi } from "../actions/catalogInQuery";
import { upsertCatalogItems } from "../reducers/catalogItemsSlice";
import { Catalog, listCatalogRequest } from "../types";

interface UseBookmarkToggleProps {
  catalogItem: Catalog;
  queryParams?: listCatalogRequest;
}

export const useBookmarkToggle = ({
  catalogItem,
  queryParams,
}: UseBookmarkToggleProps) => {
  const dispatch = useAppDispatch();
  const [patchSpecimen, { isLoading: isPatchingSpecimen }] =
    usePatchSpecimenMutation();
  const [patchCapture, { isLoading: isPatchingCapture }] =
    usePatchCaptureMutation();

  const isBookmarking = isPatchingSpecimen || isPatchingCapture;

  const handleToggleBookmark = async () => {
    const { source, specimen_uuid, capture, is_bookmarked } = catalogItem;
    const newBookmarkState = !is_bookmarked;

    let patchResult: any;

    // 1. Optimistic Update for listCatalog
    if (queryParams) {
      patchResult = dispatch(
        catalogApi.util.updateQueryData("listCatalog", queryParams, (draft) => {
          const item = draft.items.find((i) => {
            if (source === "specimen") {
              return i.specimen_uuid === specimen_uuid;
            }
            return i.capture.uuid === capture.uuid;
          });
          if (item) {
            item.is_bookmarked = newBookmarkState;
            if (source === "capture" || !item.specimen_uuid) {
              item.capture.is_bookmarked = newBookmarkState;
            }
          }
        }),
      );
    }

    try {
      if (source === "specimen") {
        await patchSpecimen({
          specimen_uuid,
          data: { is_bookmarked: newBookmarkState },
        }).unwrap();
      } else if (source === "capture") {
        await patchCapture({
          capture_uuid: capture.uuid,
          data: { is_bookmarked: newBookmarkState },
        }).unwrap();
      }

      // 2. Update individual items slice for catalog download
      dispatch(
        upsertCatalogItems([
          {
            ...catalogItem,
            capture: {
              ...capture,
              is_bookmarked:
                source === "capture" ? newBookmarkState : capture.is_bookmarked,
            },
            is_bookmarked: newBookmarkState,
          } as any,
        ]),
      );
    } catch (error) {
      // Rollback optimistic update
      if (patchResult) {
        patchResult.undo();
      }
    }
  };

  return {
    isBookmarking,
    handleToggleBookmark,
  };
};
